import { getServiceApiUrl, logApiCall, API_CONFIG } from '../config/api';
import { API_ENDPOINTS } from '../config/apiEndpoints';

// Types for admin service
export interface User {
  id?: string; // Make optional since API doesn't provide it
  username: string;
  email: string;
  // Support both camelCase (frontend forms) and snake_case (backend API)
  firstname?: string;
  lastname?: string;
  first_name?: string;
  last_name?: string;
  fullname?: string;
  full_name?: string;
  phoneNumber?: string;
  role: string;
  isActive: boolean;
  createdAt: string | null; // API can return null
  updatedAt?: string | null; // Add updatedAt field from API
  lastLogin?: string;
}

export interface UsersResponse {
  users: User[];
  total_count: number;
  page: number;
  size: number;
}

export interface UserStats {
  totalUsers: number;
  activeUsers: number;
  adminUsers: number;
  newUsersThisMonth: number;
  activeSessions: number;
}

// Admin service class
class AdminService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_AUTH_BASE_URL;
  }

  /**
   * Get authentication headers
   */
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem(API_CONFIG.AUTH.TOKEN_KEY);
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `${API_CONFIG.AUTH.HEADER_PREFIX} ${token}` }),
    };
  }

  /**
   * Handle API response
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    return data;
  }

  /**
   * Fetch all users from admin endpoint
   */
  async getAllUsers(page: number = 1, size: number = 10): Promise<UsersResponse> {
    const url = `${this.baseUrl}${API_ENDPOINTS.USERS.LIST}?page=${page}&size=${size}`;
    console.log('AdminService.getAllUsers called with:', { page, size });
    console.log('Constructed URL:', url);
    console.log('Base URL:', this.baseUrl);
    logApiCall('GET', url);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
        signal: AbortSignal.timeout(API_CONFIG.TIMEOUT),
      });

      const data = await this.handleResponse<UsersResponse>(response);
      
      // Validate response structure
      if (!data || typeof data !== 'object' || !Array.isArray(data.users)) {
        console.error('API returned invalid data structure for users:', data);
        throw new Error('Invalid response format: expected object with users array');
      }
      
      return data;
    } catch (error) {
      console.error('Failed to fetch users from API:', error);
      console.error('API URL:', url);
      console.error('Headers:', this.getAuthHeaders());
      // Don't return mock data - let the error propagate
      throw error;
    }
  }

  /**
   * Fetch all users (legacy method for backward compatibility)
   */
  async getAllUsersLegacy(): Promise<User[]> {
    const response = await this.getAllUsers(1, 1000); // Get a large number to fetch all users
    return response.users;
  }

  /**
   * Get user statistics
   */
  async getUserStats(): Promise<UserStats> {
    try {
      const users = await this.getAllUsersLegacy(); // Use legacy method to get all users
      const now = new Date();
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      const stats: UserStats = {
        totalUsers: users.length,
        activeUsers: users.filter(user => user.isActive).length,
        adminUsers: users.filter(user => user.role === 'admin').length,
        newUsersThisMonth: users.filter(user => 
          user.createdAt && new Date(user.createdAt) >= thisMonth
        ).length,
        activeSessions: users.filter(user => 
          user.lastLogin && 
          new Date(user.lastLogin) > new Date(Date.now() - 24 * 60 * 60 * 1000)
        ).length,
      };

      return stats;
    } catch (error) {
      console.error('Failed to calculate user stats:', error);
      // Don't return mock stats - let the error propagate
      throw error;
    }
  }

  /**
   * Create a new user (admin functionality)
   */
  async createUser(userData: Partial<User>): Promise<User> {
    const url = `${this.baseUrl}${API_ENDPOINTS.AUTH.REGISTER}`;
    logApiCall('POST', url, userData);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(userData),
        signal: AbortSignal.timeout(API_CONFIG.TIMEOUT),
      });

      return await this.handleResponse<User>(response);
    } catch (error) {
      console.error('Failed to create user:', error);
      throw error;
    }
  }

  /**
   * Update user (admin functionality)
   */
  async updateUser(email: string, userData: {
    username?: string;
    role?: string;
    isActive?: boolean;
    firstname?: string;
    lastname?: string;
    phonenumber?: string;
  }): Promise<User> {
    const url = `${this.baseUrl}${API_ENDPOINTS.ADMIN.UPDATE_USERS}`;
    const requestData = { 
      email,
      username: userData.username,
      role: userData.role,
      isActive: userData.isActive,
      firstname: userData.firstname,
      lastname: userData.lastname,
      phonenumber: userData.phonenumber
    };
    logApiCall('PUT', url, requestData);

    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(requestData),
        signal: AbortSignal.timeout(API_CONFIG.TIMEOUT),
      });

      return await this.handleResponse<User>(response);
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error;
    }
  }

  /**
   * Delete user (admin functionality)
   */
  async deleteUser(email: string): Promise<void> {
    const url = `${this.baseUrl}${API_ENDPOINTS.ADMIN.DELETE_USER}`;
    const requestData = { 
      email: email
    };
    logApiCall('DELETE', url, requestData);

    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(requestData),
        signal: AbortSignal.timeout(API_CONFIG.TIMEOUT),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete user: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const adminService = new AdminService();
export default adminService;